import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

/**
 * Converts a UTC timestamp to the target timezone (overridden by VITE_TIME_ZONE or browser local) and formats it.
 * @param {string|Date} date - The UTC date string or Date object.
 * @returns {string} Formatted date string.
 */
export const formatLocalTime = (date) => {
    if (!date) return '—';
    const tz = import.meta.env.VITE_TIME_ZONE;
    if (tz && tz !== 'local') {
        const match = tz.match(/^([+-]?\d+):(\d+)$/);
        if (match) {
            const hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2], 10);
            const offset = hours + (minutes / 60);
            return dayjs.utc(date).utcOffset(offset).format('MMM D, YYYY h:mm A');
        }
    }
    return dayjs.utc(date).local().format('MMM D, YYYY h:mm A');
};

/**
 * Returns only the date portion (no time), respecting the institutional timezone.
 * @param {string|Date} date
 * @returns {string} e.g. "Apr 30, 2026"
 */
export const formatDateOnly = (date) => {
    if (!date) return '—';
    const tz = import.meta.env.VITE_TIME_ZONE;
    if (tz && tz !== 'local') {
        const match = tz.match(/^([+-]?\d+):(\d+)$/);
        if (match) {
            const hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2], 10);
            const offset = hours + (minutes / 60);
            return dayjs.utc(date).utcOffset(offset).format('MMM D, YYYY');
        }
    }
    return dayjs.utc(date).local().format('MMM D, YYYY');
};

/**
 * Returns the relative time string (e.g., "5 minutes ago").
 * @param {string|Date} date 
 * @returns {string}
 */
export const formatTimeAgo = (date) => {
    if (!date) return '';
    const tz = import.meta.env.VITE_TIME_ZONE;
    if (tz && tz !== 'local') {
        const match = tz.match(/^([+-]?\d+):(\d+)$/);
        if (match) {
            const hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2], 10);
            const offset = hours + (minutes / 60);
            return dayjs.utc(date).utcOffset(offset).fromNow();
        }
    }
    return dayjs.utc(date).local().fromNow();
};

/**
 * Returns the current clock time formatted for the dashboard.
 * @param {Date|string} date 
 * @returns {string} HH:mm:ss
 */
export const formatClockTime = (date) => {
    if (!date) return '--:--:--';
    const tz = import.meta.env.VITE_TIME_ZONE;
    if (tz && tz !== 'local') {
        const match = tz.match(/^([+-]?\d+):(\d+)$/);
        if (match) {
            const hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2], 10);
            const offset = hours + (minutes / 60);
            return dayjs.utc(date).utcOffset(offset).format('HH:mm:ss');
        }
    }
    return dayjs(date).format('HH:mm:ss');
};

/**
 * Returns the current clock time in UTC format.
 * @param {Date|string} date 
 * @returns {string} HH:mm:ss (UTC)
 */
export const formatClockTimeUTC = (date) => {
    if (!date) return '--:--:--';
    return dayjs.utc(date).format('HH:mm:ss');
};

/**
 * Returns the date portion in UTC format.
 * @param {Date|string} date 
 * @returns {string} MMM D, YYYY (UTC)
 */
export const formatDateOnlyUTC = (date) => {
    if (!date) return '—';
    return dayjs.utc(date).format('MMM D, YYYY');
};

/**
 * Returns the user's detected local timezone.
 * @returns {string} Timezone string (e.g., "America/New_York").
 */
export const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export default dayjs;
